<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, useCssModule, watch } from 'vue';

  import { KEY_CODES } from '../../constants';
  import vClickoutside from '../../directives/clickoutside/clickoutside';
  import calculateElementOverflow from '../../utils/calculateElementOverflow';
  import { getMountPoint, getOwnerDomContext } from '../../utils/helpers';
  import Icon from '../Icon/Icon.vue';

  export type DropdownOffset = {
    x?: number;
    y?: number;
  };

  export interface DropdownProps {
    /**
     * Used to position the dropdown relative to target
     * Options: left, right
     */
    align?: 'left' | 'right';
    /**
     * When passed, text with caret is displayed instead of default kebab menu.
     */
    label?: string;
    /**
     * If true, dropdown will remain open when clicking on its content. Otherwise, it'll close automatically on click.
     */
    closeManually?: boolean;
    /**
     * Used to horizontally and vertically offset the dropdown in pixels
     * Keep in mind, `x` value is relative to `align` value.
     */
    offset?: DropdownOffset;
    /**
     * If `reattach` is true, the contents will be mounted in the mount point container,
     * so as to circumvent clipping issues from a parent's overflow property.
     */
    reattach?: boolean;
    /**
     * Custom class to apply to the default dropdown content element
     */
    contentClass?: string | string[];
    /**
     * Removes content container's max width
     */
    fluidContent?: boolean;
  }

  type DropdownStyles = {
    left?: string;
    right?: string;
    top?: string;
  };

  defineOptions({
    name: 'll-dropdown',
  });

  const props = withDefaults(defineProps<DropdownProps>(), {
    align: 'right',
    label: '',
    closeManually: false,
    offset: () => ({}),
    reattach: true,
    contentClass: '',
    fluidContent: false,
  });

  const emit = defineEmits<{
    toggle: [isActive: boolean];
    dismiss: [];
  }>();

  const contentRef = ref<HTMLElement | null>(null);
  const dropdownRef = ref<HTMLDivElement | null>(null);

  const DEFAULT_ITEMS_LIST = [] as unknown as NodeListOf<Element>;

  const items = ref<NodeListOf<Element>>(DEFAULT_ITEMS_LIST);
  const itemIndex = ref(-1);
  const isActive = ref(false);

  const styles = ref<DropdownStyles>({});
  const classes = useCssModule();

  watch(isActive, (val) => {
    if (!val) {
      itemIndex.value = -1;
    }

    emit('toggle', val);
  });

  watch(itemIndex, (next, prev) => {
    if (next in items.value) {
      items.value[next].classList.add('bg-ice-200');
    }
    if (prev in items.value) {
      items.value[prev].classList.remove('bg-ice-200');
    }
  });

  onMounted(() => {
    if (props.reattach) {
      // Mount in the trigger's own document so iframe-mounted apps reattach correctly.
      const { ownerDoc } = getOwnerDomContext(dropdownRef.value);
      const target = getMountPoint(ownerDoc);

      if (contentRef.value) {
        target.appendChild(contentRef.value);
      }
    }

    // if stash-menu exists, use it's items
    if (contentRef.value?.querySelector('.stash-menu')) {
      items.value = contentRef.value?.querySelectorAll('.stash-menu-item') ?? DEFAULT_ITEMS_LIST;
    } else {
      items.value = contentRef.value?.querySelectorAll('.dropdown__item') ?? DEFAULT_ITEMS_LIST;
    }

    // avoid calling dismiss with ResizeEvent because it's target is the Window element and it's not a valid Node element to be passed to dismiss
    window.addEventListener('resize', () => dismiss());
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', () => dismiss());
    contentRef?.value?.parentNode?.removeChild(contentRef.value);
  });

  /**
   * Dismisses the Dropdown.
   */
  function dismiss(event?: Event) {
    if (
      props.closeManually &&
      (contentRef.value?.contains(event?.target as HTMLElement) || contentRef.value === event?.target)
    ) {
      return;
    }

    const wasOpen = isActive.value;

    isActive.value = false;

    if (wasOpen) {
      emit('dismiss');
    }
  }

  /**
   * Opens or closes the Dropdown. If opening, positions and focuses its content.
   */
  async function toggle() {
    if (isActive.value) {
      isActive.value = false;
    } else {
      isActive.value = true;
      await calculateOffset();
      contentRef.value?.focus();
    }
  }

  /**
   * Handles all user keyboard input on the Dropdown.
   * @param {KeyboardEvent} e The native keydown event.
   */
  function onKeyDown(e: KeyboardEvent) {
    if (e.keyCode === KEY_CODES.ESCAPE) {
      dismiss();
    } else if (e.keyCode === KEY_CODES.DOWN && itemIndex.value < items.value.length - 1 && isActive.value) {
      itemIndex.value++;
    } else if (e.keyCode === KEY_CODES.UP && itemIndex.value > -1 && isActive.value) {
      itemIndex.value--;
    } else if (e.keyCode === KEY_CODES.ENTER && itemIndex.value !== -1 && isActive.value) {
      items[itemIndex.value].click();
    } else {
      return;
    }

    e.preventDefault(); // if keyDown did something / we didn't return
  }

  /**
   * Reset any page overflow offset
   */
  function resetOffset() {
    contentRef.value?.style.setProperty('--offset', '0');
  }

  /**
   * Calculate the position for the Dropdown content element,
   * relative to the viewport.
   * Repositions the dropdown content element (& arrow)
   * if it overflows the page (including gutter).
   */
  async function calculateOffset() {
    await nextTick();

    // Use the trigger's document/window so position math matches the mount target
    // when the trigger lives in a different document than the Vue runtime.
    const { ownerDoc, ownerWin } = getOwnerDomContext(dropdownRef.value);

    const toggleBCR = dropdownRef.value?.getBoundingClientRect() || ({} as DOMRect);
    const top = toggleBCR.top + toggleBCR.height;
    const offset = {
      x: 0,
      y: 0,
      ...props.offset,
    };

    if (props.reattach) {
      styles.value = {
        left: props.align === 'left' ? `${Math.round(toggleBCR.left) + offset.x}px` : undefined,
        right:
          props.align === 'right'
            ? `${ownerDoc.documentElement.clientWidth - Math.round(toggleBCR.right) + offset.x}px`
            : undefined,
        top: `${ownerWin.scrollY + top + offset.y}px`,
      };
      await nextTick(); // wait for style updates to sync in the DOM
    }

    const overflow = calculateElementOverflow(contentRef?.value);

    if (overflow) {
      const direction = props.align === 'left' ? '-' : '';
      contentRef.value?.style.setProperty('--offset', `${direction}${overflow.value}`);
    }
  }

  defineExpose({
    isActive: computed(() => isActive.value),
    toggle,
    dismiss,
  });
</script>

<template>
  <div
    ref="dropdownRef"
    v-clickoutside="dismiss"
    class="relative inline-block"
    data-test="ll-dropdown"
    @keydown="onKeyDown"
  >
    <!-- @slot Toggle component slot, receives `toggle` method to trigger Dropdown display -->
    <slot name="toggle" :is-active="isActive" :toggle="toggle">
      <button
        data-test="button|toggle"
        :aria-expanded="isActive"
        :class="[label ? 'outline-hidden min-w-auto' : 'rounded p-3']"
        @click="toggle"
      >
        <span v-if="label" class="flex items-center font-medium text-blue-500 hover:text-blue-700">
          {{ label }}
          <Icon name="caret-down" />
        </span>
        <Icon v-else name="ellipsis" />
      </button>
    </slot>

    <transition name="fade" @after-leave="resetOffset">
      <div
        v-show="isActive"
        ref="contentRef"
        data-test="ll-dropdown-content"
        :class="[
          classes.content,
          contentClass,
          {
            'left-0 after:left-6': props.align === 'left',
            'right-0': props.align === 'right',
            'max-w-[360px]': !props.fluidContent,
          },
        ]"
        :style="styles"
      >
        <!-- @slot Dropdown content slot. Receives `dismiss` method, to trigger manual close. You must call dismiss with parentheses. Receives `is-active` prop.  -->
        <slot :dismiss="dismiss" :is-active="isActive"></slot>
      </div>
    </transition>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  .content {
    --offset: 0;

    @apply shadow-2xl
    bg-white
    border
    border-solid
    border-blue-500
    rounded
    absolute
    z-[301]
    min-w-[200px];

    transform: translateX(calc(var(--offset) * 1px));
  }

  /**
* TODO: these global styles should be componentized in https://leaflink.atlassian.net/browse/STASH-566
*/
  :global(.dropdown__list) {
    @apply bg-white list-none m-1.5 p-0;
  }

  :global(.dropdown__item) {
    @apply block mx-0 mt-0 mb-1.5 w-full first:mt-0 last:mb-0;
  }

  :global(.dropdown__item) button {
    @apply border-none text-left p-1.5;
  }

  :global(.dropdown__item) :where(a, button) {
    @apply rounded
  text-ice-700
    block
    py-[10px]
    px-1.5
    w-full
    disabled:text-ice-500
    disabled:cursor-default
    hover:bg-ice-200
    hover:text-ice-700
    hover:no-underline
    transition-all
    duration-150
    ease-swing;
  }
</style>
